import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import useAutosizeTextArea from '../../hooks/useAutosizeTextArea';
import Image from '../Image';
import Modal from '../Modal';

const SceneEditing = forwardRef(({ scenes, setIsGenerating, proceed, videos, generationModel, aspectRatio }, ref) => {
  const [internalScenes, setInternalScenes] = useState([]);
  const [editingSceneId, setEditingSceneId] = useState(null);
  const [generatingSceneIds, setGeneratingSceneIds] = useState([]);
  const [generatingVideoSceneIds, setGeneratingVideoSceneIds] = useState([]);
  const [errorSceneIds, setErrorSceneIds] = useState([]);

  // 替换 selectedImage 状态，并为图片编辑模态框新增 state
  const [selectedSceneForModal, setSelectedSceneForModal] = useState(null);
  const [isEditModeActive, setIsEditModeActive] = useState(false);
  const [imageEditPrompt, setImageEditPrompt] = useState('');
  const [editingImageSceneIds, setEditingImageSceneIds] = useState([]);

  const [showVideoModal, setShowVideoModal] = useState(false);
  const [selectedVideoScene, setSelectedVideoScene] = useState(null);
  const textAreaRef = useRef(null);

  useAutosizeTextArea(textAreaRef.current, internalScenes?.find(s => s.id === editingSceneId)?.scene);

  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    if (videos && videos.length > 0) {
      setInternalScenes(videos);
    } else if (scenes) {
      const newInternalScenes = scenes.map(s => ({
        ...s,
        displayImageUrl: s.imageUrl,
        gcsImageUrl: s.gcsImageUrl || null
      }));
      setInternalScenes(newInternalScenes);
    }
  }, [scenes, videos]);

  useEffect(() => {
    // 将 editingImageSceneIds 也加入依赖项，以控制全局加载状态
    setIsGenerating(generatingSceneIds.length > 0 || generatingVideoSceneIds.length > 0 || editingImageSceneIds.length > 0);
  }, [generatingSceneIds, generatingVideoSceneIds, editingImageSceneIds, setIsGenerating]);

  const [allSelected, setAllSelected] = useState(true);

  const handleSelectAll = (e) => {
    const checked = e.target.checked;
    setAllSelected(checked);
    setInternalScenes(internalScenes.map(scene => ({ ...scene, isSelected: checked })));
  };

  const handleSelectOne = (id) => {
    const newScenes = internalScenes.map(scene => {
      if (scene.id === id) {
        return { ...scene, isSelected: !scene.isSelected };
      }
      return scene;
    });
    setInternalScenes(newScenes);
    setAllSelected(newScenes.every(s => s.isSelected));
  };

  const handleSceneTextClick = (id) => {
    setEditingSceneId(id);
  };

  const handleSceneTextChange = (e, id) => {
    const newScenes = internalScenes.map(scene => {
      if (scene.id === id) {
        return { ...scene, scene: e.target.value };
      }
      return scene;
    });
    setInternalScenes(newScenes);
  };

  const handleSceneTextBlur = () => {
    setEditingSceneId(null);
  };

  // 修改 handleImageClick 以接收整个 scene 对象
  const handleImageClick = (scene) => {
    setSelectedSceneForModal(scene);
  };

  // 扩展 handleCloseModal 以重置所有相关状态
  const handleCloseModal = () => {
    setSelectedSceneForModal(null);
    setIsEditModeActive(false);
    setImageEditPrompt('');
  };

  const handleClearImage = (sceneId) => {
    setInternalScenes(prevScenes => {
      return prevScenes.map(s => {
        if (s.id === sceneId) {
          return { ...s, displayImageUrl: null, gcsImageUrl: null };
        }
        return s;
      });
    });
  };

  const handleClearVideo = (sceneId) => {
    setInternalScenes(prevScenes => {
      return prevScenes.map(s => {
        if (s.id === sceneId) {
          return { ...s, videoUrl: null };
        }
        return s;
      });
    });
  };

  const handleGenerateImage = async (sceneId) => {
    const scene = internalScenes.find(s => s.id === sceneId);
    if (!scene) return;

    const fullPrompt = `Here is a scene description: ${scene.description || scene.scene}. Please generate an image as input frame for the video.`;

    setGeneratingSceneIds(prev => [...prev, sceneId]);
    setErrorSceneIds(prev => prev.filter(id => id !== sceneId));

    try {
      const response = await fetch(`${apiBaseUrl}/api/generate-image`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: fullPrompt,
          aspectRatio: aspectRatio
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        alert(`Image generation failed: ${errorData.detail || 'An unknown server error occurred.'}`);
        setErrorSceneIds(prev => [...prev, sceneId]);
        throw new Error('Failed to generate image');
      }

      const data = await response.json();
      const { displayImageUrl, gcsImageUrl } = data;

      setInternalScenes(prevScenes => {
        return prevScenes.map(s => s.id === sceneId ? { ...s, displayImageUrl, gcsImageUrl } : s);
      });
    } catch (error) {
      console.error('Error generating image:', error);
      if (!String(error.message).includes('Failed to generate image')) {
          alert('An unexpected error occurred. Please check the console for details.');
      }
    } finally {
      setGeneratingSceneIds(prev => prev.filter(id => id !== sceneId));
    }
  };

  const handleImageUpload = async (event, sceneId) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    setGeneratingSceneIds(prev => [...prev, sceneId]);
    setErrorSceneIds(prev => prev.filter(id => id !== sceneId));

    try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${apiBaseUrl}/api/upload-image`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
            body: formData,
        });

        if (!response.ok) {
            const errorData = await response.json();
            alert(`Image upload failed: ${errorData.detail || 'An unknown server error occurred.'}`);
            setErrorSceneIds(prev => [...prev, sceneId]);
            throw new Error('Failed to upload image');
        }

        const data = await response.json();
        const { displayImageUrl, gcsImageUrl } = data;

        setInternalScenes(prevScenes => {
            return prevScenes.map(s => s.id === sceneId ? { ...s, displayImageUrl, gcsImageUrl } : s);
        });
        alert('Image uploaded successfully!');
    } catch (error) {
        console.error('Error uploading image:', error);
        if (!String(error.message).includes('Failed to upload image')) {
            alert('An unexpected error occurred during image upload. Please check the console for details.');
        }
    } finally {
        setGeneratingSceneIds(prev => prev.filter(id => id !== sceneId));
    }
  };


  const handleActivateEditMode = () => {
    setIsEditModeActive(true);
  };

  const handleCancelEditMode = () => {
    setIsEditModeActive(false);
    setImageEditPrompt('');
  };

  const handleEditImageSubmit = async () => {
    if (!selectedSceneForModal) return;
    if (!imageEditPrompt.trim()) {
      alert('Please input your prompt for image editing.');
      return;
    }

    const sceneId = selectedSceneForModal.id;

    setEditingImageSceneIds(prev => [...prev, sceneId]);
    setErrorSceneIds(prev => prev.filter(id => id !== sceneId));

    try {
      const payload = {
        prompt: imageEditPrompt,
        image_uris: [selectedSceneForModal.gcsImageUrl],
      };


      const response = await fetch(`${apiBaseUrl}/api/edit-image`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        alert(`Image editing failed: ${errorData.detail || 'An unknown server error occurred.'}`);
        setErrorSceneIds(prev => [...prev, sceneId]);
        throw new Error('Failed to edit image');
      }

      const data = await response.json();
      const { displayImageUrl, gcsImageUrl } = data;

      setInternalScenes(prevScenes =>
        prevScenes.map(s =>
          s.id === sceneId ? { ...s, displayImageUrl, gcsImageUrl } : s
        )
      );

      handleCloseModal();
      alert('Image edited successfully!');

    } catch (error) {
      console.error('Error editing image:', error);
      if (!String(error.message).includes('Failed to edit image')) {
          alert('An unexpected error occurred during image editing. Please check the console for details.');
      }
    } finally {
      setEditingImageSceneIds(prev => prev.filter(id => id !== sceneId));
    }
  };


  const handleGenerateVideo = async (sceneId) => {
    const scene = internalScenes.find(s => s.id === sceneId);
    if (!scene) return;
    handleGenerateAllVideos([scene]);
  };

  const handleGenerateAllImages = () => {
    const selectedScenes = internalScenes.filter(s => s.isSelected);
    Promise.all(selectedScenes.map(scene => handleGenerateImage(scene.id)));
  };

  const handleGenerateAllVideos = async (scenesToGenerate) => {
    const scenesArray = Array.isArray(scenesToGenerate) ? scenesToGenerate : null;
    const selectedScenes = scenesArray || (internalScenes ? internalScenes.filter(s => s.isSelected) : []);
    if (!selectedScenes || selectedScenes.length === 0) {
      alert('Please select at least one scene to generate video.');
      return;
    }

    const promptsPayload = selectedScenes.map(s => ({
      prompt: s.scene,
      image_uri: s.gcsImageUrl,
    }));

    const sceneIds = selectedScenes.map(s => s.id);

    setGeneratingVideoSceneIds(prev => [...new Set([...prev, ...sceneIds])]);
    setErrorSceneIds(prev => prev.filter(id => !sceneIds.includes(id)));

    try {
      const response = await fetch(`${apiBaseUrl}/api/generate-video`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompts: promptsPayload,
          generationModel: generationModel,
          aspectRatio: aspectRatio
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        alert(`Server error: ${errorData.error || 'Failed to generate videos.'}`);
        setErrorSceneIds(prev => [...new Set([...prev, ...sceneIds])]);
        throw new Error('Server returned an error');
      }

      const data = await response.json();
      const results = data.results;

      const errorMessages = [];
      const newFailedSceneIds = [];

      setInternalScenes(currentScenes => {
        return currentScenes.map(scene => {
          const resultIndex = selectedScenes.findIndex(s => s.id === scene.id);
          if (resultIndex !== -1) {
            const result = results[resultIndex];
            if (result.status === 'success') {
              return { ...scene, videoUrl: result.path };
            } else {
              const sceneTitle = scene.scene.substring(0, 40);
              errorMessages.push({ title: sceneTitle, message: result.message });
              newFailedSceneIds.push(scene.id);
              return { ...scene, videoUrl: null };
            }
          }
          return scene;
        });
      });

      if (newFailedSceneIds.length > 0) {
        setErrorSceneIds(prevErrorIds => [...new Set([...prevErrorIds, ...newFailedSceneIds])]);
      }

      if (errorMessages.length > 0) {
        const formattedErrors = errorMessages.map(err => {
            let userFriendlyMessage = err.message;
            if (typeof err.message === 'string' && err.message.includes("violate")) {
                userFriendlyMessage = "The video could not be generated because the prompt or reference image violated Vertex AI's safety policy. Please avoid words or descriptions related to violence, self-harm, or hateful content that violate Vertex AI's safety policy; try to rephrase the script, or use a different image, or contact your Google representative to request allowlisting.";
            }
            if (typeof err.message === 'string' && err.message.includes("Content is filtered due to unknown reasons")) {
                userFriendlyMessage = "Content is filtered due to unknown reasons. Please avoid words or descriptions related to violence, self-harm, or hateful content that violate Vertex AI's safety policy; try to rephrase the script, or use a different image, or contact your Google representative to request allowlisting.";
            }
            return `- Scene starting with "${err.title}...":\n\n  ${userFriendlyMessage}`;
        }).join('\n\n');

        alert(`Video generation failed for ${errorMessages.length} scene(s):\n\n${formattedErrors}`);
      }

    } catch (error) {
      console.error('Error generating videos:', error);
      alert('An unexpected error occurred while generating videos. Please check the console.');
    } finally {
      setGeneratingVideoSceneIds(prev => prev.filter(id => !sceneIds.includes(id)));
    }
  };

  const handleUploadVideo = async (event, sceneId) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('video', file);

    setGeneratingVideoSceneIds(prev => [...prev, sceneId]);
    setErrorSceneIds(prev => prev.filter(id => id !== sceneId));

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiBaseUrl}/api/upload-video`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });

      if (!response.ok) {
        setErrorSceneIds(prev => [...prev, sceneId]);
        throw new Error('Failed to upload video');
      }

      const data = await response.json();
      const videoUrl = data.videoPath;

      setInternalScenes(prevScenes => {
        return prevScenes.map(s => s.id === sceneId ? { ...s, videoUrl } : s);
      });
    } catch (error) {
      console.error('Error uploading video:', error);
    } finally {
      setGeneratingVideoSceneIds(prev => prev.filter(id => id !== sceneId));
    }
  };

  const handleMergeVideos = async () => {
    const selectedVideos = internalScenes.filter(s => s.isSelected && s.videoUrl).map(s => s.videoUrl);
    if (selectedVideos.length === 0) {
      alert('Please select at least one video to merge.');
      return;
    }

    try {
      const response = await fetch(`${apiBaseUrl}/api/merge_videos`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ video_paths: selectedVideos }),
      });

      if (!response.ok) {
        throw new Error('Failed to merge videos');
      }

      const data = await response.json();
      proceed(data.merged_video_path, internalScenes);
    } catch (error) {
      console.error('Error merging videos:', error);
      alert('Failed to merge videos. Please try again.');
    }
  };

  const handleAddScene = () => {
    if (internalScenes.length >= 10) {
      alert('You can add a maximum of 10 scenes.');
      return;
    }
    const newScene = {
      id: `new-scene-${Date.now()}`,
      scene: 'A new scene',
      isSelected: true,
      imageUrl: null,
      gcsImageUrl: null,
      videoUrl: null,
    };
    setInternalScenes(prev => [...prev, newScene]);
  };

  const handleRemoveScene = (id) => {
    setInternalScenes(internalScenes.filter(scene => scene.id !== id));
  };

  useImperativeHandle(ref, () => ({
    handleNext: () => {
      if (!internalScenes) {
        alert('Please wait for scenes to load.');
        return;
      }
      const selectedScenesWithVideo = internalScenes.filter(s => s.isSelected && s.videoUrl);

      if (selectedScenesWithVideo.length === 0) {
        alert('Please select a scene with a generated video to proceed.');
        return;
      }

      setSelectedVideoScene(selectedScenesWithVideo[0]);
      setShowVideoModal(true);
    }
  }));

  // 辅助函数，用于统一检查图片是否在加载中
  const isImageLoading = (sceneId) => {
    return generatingSceneIds.includes(sceneId) || editingImageSceneIds.includes(sceneId);
  }

  return (
    <div className="p-8 bg-white min-h-screen">
      <table className="w-full text-xs text-left text-gray-500 dark:text-gray-400 table-fixed">
        <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
          <tr>
            <th scope="col" className="p-4 w-[5%]">
              <div className="flex items-center">
                <input id="checkbox-all-search" type="checkbox" checked={allSelected} onChange={handleSelectAll} className="w-4 h-4 text-blue-600 bg-gray-100 rounded border-gray-300 focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600" />
                <label htmlFor="checkbox-all-search" className="sr-only">checkbox</label>
              </div>
            </th>
            <th scope="col" className="py-3 px-6 w-[50%] text-center">Scene</th>
            <th scope="col" className="py-3 px-6 w-[20%] text-center">
              <div className="flex items-center justify-center">
                Starting frame
                <button onClick={handleGenerateAllImages} className="ml-2 p-1 rounded-full bg-gray-200 hover:bg-gray-300" title="Generate for all selected scenes">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-5 h-5"><path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456Z" /></svg>
                </button>
              </div>
            </th>
            <th scope="col" className="py-3 px-6 w-[25%]">
              <div className="flex items-center justify-center">
                Generated Video
                <button onClick={() => handleGenerateAllVideos()} className="ml-2 p-1 rounded-full bg-gray-200 hover:bg-gray-300" title="Generate for all selected scenes">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6"><path strokeLinecap="round" strokeLinejoin="round" d="m15.75 10.5 4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9A2.25 2.25 0 0 0 13.5 5.25h-9A2.25 2.25 0 0 0 2.25 7.5v9A2.25 2.25 0 0 0 4.5 18.75Z" /></svg>
                </button>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {internalScenes && internalScenes.map((scene) => (
            <tr key={scene.id} className="bg-white border-b dark:bg-gray-800 dark:border-gray-700">
              <td className="p-4">
                <div className="flex items-center">
                  <input id={`checkbox-table-search-${scene.id}`} type="checkbox" checked={!!scene.isSelected} onChange={() => handleSelectOne(scene.id)} className="w-4 h-4 text-blue-600 bg-gray-100 rounded border-gray-300 focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600" />
                  <label htmlFor={`checkbox-table-search-${scene.id}`} className="sr-only">checkbox</label>
                  <button onClick={() => handleRemoveScene(scene.id)} className="ml-2 text-red-500 hover:text-red-700">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                  </button>
                </div>
              </td>
              <td className="py-4 px-6">
                {editingSceneId === scene.id ? (
                  <textarea ref={textAreaRef} value={scene.scene} onChange={(e) => handleSceneTextChange(e, scene.id)} onBlur={handleSceneTextBlur} className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 min-h-[192px] whitespace-pre-wrap" autoFocus />
                ) : (
                  <span onClick={() => handleSceneTextClick(scene.id)} className="whitespace-pre-wrap w-full">{scene.scene}</span>
                )}
              </td>
              <td className="py-4 px-6 align-top">
                <div className="w-40 mx-auto">
                  {isImageLoading(scene.id) ? (
                    <div className="bg-gray-200 h-24 w-full flex items-center justify-center rounded-md"><svg className="animate-spin h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg></div>
                  ) : scene.displayImageUrl && !errorSceneIds.includes(scene.id) ? (
                    <div className="relative image-container cursor-pointer" onClick={() => handleImageClick(scene)}>
                      <Image src={scene.displayImageUrl} alt="Generated frame" className="rounded-lg shadow-md" />
                      <button onClick={(e) => { e.stopPropagation(); handleClearImage(scene.id); }} className="clear-button absolute top-1 right-1 bg-black bg-opacity-50 text-white rounded-full p-1 hover:bg-opacity-75"><svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg></button>
                    </div>
                  ) : (
                    <div className="bg-gray-200 h-24 w-full flex items-center justify-center rounded-md">
                      <div className="flex space-x-2">
                        <button onClick={() => handleGenerateImage(scene.id)} className="p-2 rounded-full bg-gray-300 hover:bg-gray-400" title="Generate Image"><svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-5 h-5"><path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456Z" /></svg></button>
                        <button onClick={() => document.getElementById(`upload-image-${scene.id}`).click()} className="p-2 rounded-full bg-gray-300 hover:bg-gray-400" title="Upload Image"><svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-5 h-5"><path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5" /></svg></button>
                        <input type="file" id={`upload-image-${scene.id}`} className="hidden" accept="image/*" onChange={(e) => handleImageUpload(e, scene.id)} />
                      </div>
                    </div>
                  )}
                </div>
              </td>
              <td className="py-4 px-6 align-top">
                <div className="w-full aspect-w-16 aspect-h-9">
                  {generatingVideoSceneIds.includes(scene.id) ? (
                    <div className="w-full h-full flex items-center justify-center bg-gray-200 rounded-md"><svg className="animate-spin h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg></div>
                  ) : scene.videoUrl && !errorSceneIds.includes(scene.id) ? (
                    <div className="relative video-container">
                      <video src={scene.videoUrl} controls className="w-full h-full object-cover rounded-lg shadow-md" />
                      <button onClick={(e) => { e.stopPropagation(); handleClearVideo(scene.id); }} className="clear-button absolute top-1 right-1 bg-transparent text-white rounded-full p-1"><svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg></button>
                    </div>
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center bg-gray-200 rounded-md">
                      <div className="flex space-x-2">
                        <button onClick={() => handleGenerateVideo(scene.id)} className="p-2 rounded-full bg-gray-300 hover:bg-gray-400" title="Generate Video"><svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6"><path strokeLinecap="round" strokeLinejoin="round" d="m15.75 10.5 4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9A2.25 2.25 0 0 0 13.5 5.25h-9A2.25 2.25 0 0 0 2.25 7.5v9A2.25 2.25 0 0 0 4.5 18.75Z" /></svg></button>
                        <button onClick={() => document.getElementById(`upload-video-${scene.id}`).click()} className="p-2 rounded-full bg-gray-300 hover:bg-gray-400" title="Upload Video"><svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-6 h-6"><path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5" /></svg></button>
                        <input type="file" id={`upload-video-${scene.id}`} className="hidden" accept="video/*" onChange={(e) => handleUploadVideo(e, scene.id)} />
                      </div>
                      <p className="text-xs text-gray-500 mt-2">Generate or upload a video</p>
                      {errorSceneIds.includes(scene.id) && <p className="text-xs text-red-500 mt-1">Generation failed. Please try again.</p>}
                    </div>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button onClick={handleAddScene} className="mt-4 text-sm font-medium text-blue-600 hover:text-blue-700 bg-blue-100 hover:bg-blue-200 rounded-full px-4 py-2 disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed" disabled={(internalScenes?.length || 0) >= 10}>
        + Add Another Scene
      </button>

      {/* 替换旧的 Modal，使用新的图片编辑模态框 */}
      <Modal
        isOpen={!!selectedSceneForModal}
        onClose={handleCloseModal}
        actions={
          selectedSceneForModal && (
            <>
              {!isEditModeActive ? (
                <button
                  onClick={handleActivateEditMode}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Edit this Image
                </button>
              ) : (
                <>
                  <button
                    onClick={handleCancelEditMode}
                    className="px-4 py-2 bg-gray-300 text-black rounded-lg hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleEditImageSubmit}
                    disabled={editingImageSceneIds.includes(selectedSceneForModal.id)}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {editingImageSceneIds.includes(selectedSceneForModal.id) ? 'Processing...' : 'Go Nano Banana!'}
                  </button>
                </>
              )}
            </>
          )
        }
      >
        {selectedSceneForModal && (
          <div>
            <div className="relative">
              <Image
                src={selectedSceneForModal.displayImageUrl}
                alt="Selected frame"
                className="max-w-full max-h-[60vh] mx-auto"
              />
              {editingImageSceneIds.includes(selectedSceneForModal.id) && (
                <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center rounded-lg">
                  <svg className="animate-spin h-10 w-10 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
              )}
            </div>
            {isEditModeActive && (
              <div className="mt-4">
                <textarea
                  value={imageEditPrompt}
                  onChange={(e) => setImageEditPrompt(e.target.value)}
                  placeholder="Use Nano Banana to re-craft your image? Please input your prompt for image editing...."
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  rows="3"
                />
              </div>
            )}
          </div>
        )}
      </Modal>

      <Modal isOpen={showVideoModal} onClose={() => setShowVideoModal(false)}>
        {selectedVideoScene && (
          <div>
            <video src={selectedVideoScene.videoUrl} controls autoPlay className="w-full" />
            <div className="flex mt-4 space-x-2 overflow-x-auto">
              {internalScenes.filter(s => s.isSelected && s.videoUrl).map(scene => (
                <div key={scene.id} className={`cursor-pointer border-2 ${selectedVideoScene.id === scene.id ? 'border-blue-500' : 'border-transparent'}`} onClick={() => setSelectedVideoScene(scene)}>
                  <video src={scene.videoUrl} className="w-24 h-16 object-cover rounded" />
                </div>
              ))}
            </div>
            <div className="flex justify-end mt-4">
              <button onClick={() => setShowVideoModal(false)} className="px-4 py-2 bg-gray-300 text-black rounded-lg mr-2">Cancel</button>
              <button onClick={() => { setShowVideoModal(false); handleMergeVideos(); }} className="px-4 py-2 bg-blue-600 text-white rounded-lg">Next</button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
});

export default SceneEditing;